import React, { useCallback, useImperativeHandle, forwardRef, useState } from 'react';
import { store } from 'Models/Store';
import { appConfigStore } from 'Models/AppConfigStore';
import {
	formatCustomerName,
	formatCustomerOption,
} from 'Views/Components/_HumanWritten/CustomerSelection/CustomerOptionFormatter';
import { useCustomerSearch } from 'Views/Components/_HumanWritten/CustomerSelection/Queries/useCustomerSearch';
import { AsyncSelectInput } from 'Views/Components/New/AsyncSelectInput';
import { AddNewCustomerButton } from 'Views/Components/_HumanWritten/CustomerSelection/AddNewCustomerButton';
import { NumberInput } from 'Views/Components/New/NumberInput';
import { TextInput } from 'Views/Components/New/TextInput';
import { DateInput } from 'Views/Components/New/DateInput';
import { TextArea } from 'Views/Components/New/TextArea';
import { RadioAlignment, RadioButtonGroup } from 'Views/Components/RadioButton/RadioButtonGroup';
import { formatStandardDate } from 'Util/_HumanWritten/DateUtils';
import { getExpiryDuration } from 'Util/_HumanWritten/VoucherUtils';
import { isEmail } from 'Validators/Functions/Email';
import { UserEntity } from 'Models/Entities';
import moment from 'moment';

export type GiftCertificateGiftType = 'for-me' | 'for-customer' | 'gift';

export interface GiftCertificateFormSubmitData {
	amount: number;
	isGift: boolean;
	giftType: GiftCertificateGiftType;
	recipientEmail?: string;
	message?: string;
	userId?: string;
	customerName?: string;
	expiryDate?: Date;
	/** The full selected customer entity, kept so the customer select can be restored on re-mount. */
	selectedCustomer?: UserEntity;
}

export interface GiftCertificateFormRef {
	submit: () => void;
	/** Snapshot of the current form values without validating — used to restore the form after a detour step. */
	capture: () => Partial<GiftCertificateFormSubmitData>;
}

interface GiftCertificateFormProps {
	onSubmit: (data: GiftCertificateFormSubmitData) => void;
	initialValues?: Partial<GiftCertificateFormSubmitData>;
	onAddNewCustomer?: () => void;
}

interface FormErrors {
	amount?: string;
	recipientEmail?: string;
	selectedUserId?: string;
}

const GiftCertificateForm = forwardRef<GiftCertificateFormRef, GiftCertificateFormProps>(
	({ onSubmit, initialValues, onAddNewCustomer }, ref) => {
		const { isStaff, isManager } = store;
		const { loadOptions } = useCustomerSearch();

		const defaultExpiry = moment().add(appConfigStore.giftCertificateExpiryYears, 'years').format('YYYY-MM-DD');

		const [giftType, setGiftType] = useState<GiftCertificateGiftType>(
			initialValues?.giftType ?? (isStaff ? 'for-customer' : 'for-me'),
		);
		const [amount, setAmount] = useState<number | undefined>(initialValues?.amount);
		const [recipientEmail, setRecipientEmail] = useState(initialValues?.recipientEmail ?? '');
		const [message, setMessage] = useState(initialValues?.message ?? '');
		const [selectedCustomer, setSelectedCustomer] = useState<UserEntity | null>(
			initialValues?.selectedCustomer ?? null,
		);
		const [selectedUserId, setSelectedUserId] = useState<string | undefined>(initialValues?.userId);
		const [customerName, setCustomerName] = useState(initialValues?.customerName ?? '');
		const [expiryDate, setExpiryDate] = useState(
			initialValues?.expiryDate ? moment(initialValues.expiryDate).format('YYYY-MM-DD') : defaultExpiry,
		);
		const [errors, setErrors] = useState<FormErrors>({});

		// MobX model for RadioButtonGroup (it requires model/modelProperty)
		const radioModel = { isGift: giftType };

		const handleSubmit = () => {
			const newErrors: FormErrors = {};

			if (amount === undefined || amount <= 0) {
				newErrors.amount = 'Please enter a value greater than $0';
			}

			if (giftType === 'gift') {
				if (!recipientEmail) {
					newErrors.recipientEmail = 'Please enter a recipient email address';
				} else if (!isEmail(recipientEmail)) {
					newErrors.recipientEmail = 'Please enter a valid email address';
				}
			}

			if (isStaff && (giftType === 'for-customer' || giftType === 'gift') && !selectedUserId) {
				newErrors.selectedUserId = 'Please select a customer';
			}

			setErrors(newErrors);

			if (Object.keys(newErrors).length === 0 && amount) {
				const isGift = giftType === 'gift';
				onSubmit({
					amount,
					isGift,
					giftType,
					recipientEmail: isGift ? recipientEmail : undefined,
					message: message || undefined,
					userId:
						isStaff && (giftType === 'for-customer' || giftType === 'gift') ? selectedUserId : undefined,
					customerName:
						isStaff && (giftType === 'for-customer' || giftType === 'gift') ? customerName : undefined,
					selectedCustomer:
						isStaff && (giftType === 'for-customer' || giftType === 'gift')
							? (selectedCustomer ?? undefined)
							: undefined,
					expiryDate: moment(expiryDate).toDate(),
				});
			}
		};

		useImperativeHandle(ref, () => ({
			submit: handleSubmit,
			capture: () => ({
				amount,
				isGift: giftType === 'gift',
				giftType,
				recipientEmail: recipientEmail || undefined,
				message: message || undefined,
				userId: selectedUserId,
				customerName: customerName || undefined,
				selectedCustomer: selectedCustomer ?? undefined,
				expiryDate: moment(expiryDate).toDate(),
			}),
		}));

		const radioOptions = isStaff
			? [
					{ display: 'For a customer', value: 'for-customer' },
					{ display: 'Gift', value: 'gift' },
				]
			: [
					{ display: 'For me', value: 'for-me' },
					{ display: 'Gift', value: 'gift' },
				];

		const expiryDateObj = moment(expiryDate).toDate();

		return (
			<div className="purchase-gift-certificate__form">
				<div className="flex flex-col gap-3">
					<RadioButtonGroup
						name="gift-type"
						className="gift-type-toggle"
						alignment={RadioAlignment.HORIZONTAL}
						model={radioModel}
						modelProperty="isGift"
						label="Who is this for?"
						options={radioOptions}
						onAfterChange={() => {
							setGiftType(radioModel.isGift as GiftCertificateGiftType);
							setMessage('');
							setRecipientEmail('');
							setErrors({});
						}}
					/>
					{isStaff && (giftType === 'for-customer' || giftType === 'gift') && (
						<div className="customer-select-row">
							<AsyncSelectInput
								label={giftType === 'gift' ? 'From customer' : 'Customer'}
								required
								placeholder="Search for customer name, email or phone..."
								loadOptions={loadOptions}
								defaultOptions
								cacheOptions
								isClearable
								value={selectedCustomer}
								onChange={(option: any) => {
									setSelectedCustomer(option);
									setSelectedUserId(option?.id || undefined);
									setCustomerName(option ? `${option.firstName} ${option.lastName}` : '');
									setErrors(prev => ({
										...prev,
										selectedUserId: undefined,
									}));
								}}
								getOptionLabel={(option: any) => formatCustomerName(option)}
								getOptionValue={(option: any) => option.id}
								formatOptionLabel={formatCustomerOption as any}
								error={errors.selectedUserId}
							/>
							{onAddNewCustomer && <AddNewCustomerButton onClick={onAddNewCustomer} />}
						</div>
					)}
					{giftType === 'gift' && (
						<TextInput
							label="Recipient email"
							required
							type="email"
							placeholder="Enter recipient's email address"
							value={recipientEmail}
							onChange={e => {
								setRecipientEmail(e.target.value);
								setErrors(prev => ({
									...prev,
									recipientEmail: undefined,
								}));
							}}
							error={errors.recipientEmail}
						/>
					)}
					<div className="purchase-gift-certificate__amount-expiry">
						<NumberInput
							label="Amount"
							required
							placeholder="0.00"
							value={amount ?? ''}
							allowNegative={false}
							onChange={val => {
								setAmount(val);
								setErrors(prev => ({
									...prev,
									amount: undefined,
								}));
							}}
							error={errors.amount}
						/>
						<div className="purchase-gift-certificate__expiry">
							{isManager ? (
								<DateInput
									label="Expiry date"
									value={expiryDate}
									min={moment().format('YYYY-MM-DD')}
									onChange={e => setExpiryDate(e.target.value)}
								/>
							) : (
								<TextInput label="Expiry date" value={formatStandardDate(expiryDateObj)} readOnly />
							)}
							<p className="expiry-duration">{getExpiryDuration(expiryDateObj)}</p>
						</div>
					</div>
					{giftType === 'gift' && (
						<div className="gift-certificate__message-field">
							<TextArea
								label="Personal message (optional)"
								placeholder="Add a message to include in the email"
								value={message}
								maxLength={200}
								onChange={e => setMessage(e.target.value)}
							/>
							{message && <span className="gift-certificate__char-count">{message.length}/200</span>}
						</div>
					)}
				</div>
			</div>
		);
	},
);

export default GiftCertificateForm;
